import React from 'react';
import { Property } from '../../types';

interface PropertyCardProps {
  property: Property;
  onClick: () => void;
  onInvestClick: (e: React.MouseEvent) => void;
  showInvestButton?: boolean;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ 
  property, 
  onClick, 
  onInvestClick, 
  showInvestButton = true 
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K`;
    }
    return num.toString();
  };

  const getCompletionPercentage = () => {
    if (!property.total_shares || !property.available_shares) return 0;
    return ((property.total_shares - property.available_shares) / property.total_shares * 100);
  };

  const getRemainingAmount = () => {
    if (!property.price_per_share || !property.available_shares) return 0;
    return property.price_per_share * property.available_shares;
  };

  // For "Private Credit Fund" style card from mockup
  if (property.property_type === 'Fund') {
    return (
      <div 
        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
        onClick={onClick}
      >
        {/* Fund Header Image - Collage style like mockup */}
        <div className="relative h-48 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700">
            {/* Collage background elements */}
            <div className="absolute top-0 left-0 w-1/2 h-full">
              <img
                src="/api/placeholder/300/200"
                alt="Construction"
                className="w-full h-full object-cover opacity-80"
              />
            </div>
            <div className="absolute bottom-0 right-0 w-1/2 h-2/3">
              <img
                src="/api/placeholder/300/200"
                alt="Money"
                className="w-full h-full object-cover opacity-80"
              />
            </div>
            {/* Blue diagonal overlay */}
            <div className="absolute inset-0 bg-blue-600 opacity-40 transform skew-y-12 origin-top-left scale-150"></div>
          </div>
        </div>

        {/* Fund Content */}
        <div className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {property.title}
          </h3>
          
          <p className="text-sm text-gray-500 mb-3">
            Real Estate Debt, Fund
          </p>

          <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600">
            <span>75 Loans</span>
            <span>18K Investors</span>
          </div>

          <div className="flex items-center mb-4">
            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
            <span className="text-sm text-gray-700">Available</span>
          </div>
        </div>
      </div>
    );
  }

  // Regular property card
  return (
    <div 
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      {/* Property Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={property.images?.[0] || '/api/placeholder/400/300'}
          alt={property.title}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Property Content */}
      <div className="p-4">
        {/* Property Title */}
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {property.title}
        </h3>
        
        {/* Property Location */}
        <p className="text-sm text-gray-500 mb-3">
          {property.city}, {property.state}
        </p>

        {/* Property Stats */}
        <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600">
          <span>{formatNumber(property.total_shares)} Shares</span>
          <span>{formatNumber(property.stakersCount || 0)} Investors</span>
        </div>

        {/* Status Indicator */}
        <div className="flex items-center mb-4">
          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
          <span className="text-sm text-gray-700">Available</span>
        </div>
      </div>

      {/* Investment Bar - Sticky to bottom like in mockup */}
      <div className="bg-blue-50 px-4 py-3 border-t border-gray-100">
        <div className="flex items-center justify-between text-sm mb-3">
          <div>
            <div className="text-lg font-bold text-gray-900">
              {formatCurrency(getRemainingAmount())}
            </div>
            <div className="text-gray-500 text-xs">Remaining Investment Amount</div>
          </div>
          <div className="text-right">
            <div className="flex items-center space-x-6">
              <div>
                <div className="text-lg font-bold text-gray-900">
                  {Math.round(getCompletionPercentage())}%
                </div>
                <div className="text-gray-500 text-xs">Funded</div>
              </div>
              <div className="border-l border-gray-300 pl-4">
                <div className="text-lg font-bold text-gray-900">
                  {formatNumber(property.stakersCount || 1216)}
                </div>
                <div className="text-gray-500 text-xs">Investors</div>
              </div>
            </div>
          </div>
        </div>
        
        {showInvestButton && (
          <button
            onClick={onInvestClick}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            INVEST
          </button>
        )}
      </div>
    </div>
  );
};

export default PropertyCard;
